import { useAuth0 } from '@auth0/auth0-react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  type Control,
  type FieldErrors,
  type UseFormRegister,
  useFieldArray,
} from 'react-hook-form';
import { SmallSecondaryButton } from '../../../components/buttons';
import { inputClass, inputInvalidClass } from '../../../components/form-styles';
import { Field } from '../../../components/forms';
import { Loading } from '../../../components/loading';
import { Cell, Row, Table } from '../../../components/tables';
import { type Store, UserRole, UserRoleLabel } from '../../../data/store';
import type { FormValues } from './form-data';

type UsersFormProps = {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  register: UseFormRegister<FormValues>;
  store: Store;
};

export function UsersForm({ control, errors, register, store }: UsersFormProps) {
  const { user } = useAuth0();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
  });

  if (!user) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Table headings={['Email', 'Role', '']}>
        {fields.map((field, index) => {
          const isCurrentUser = field.email === user.email;
          const isExistingUser = store.users.some(
            (user) => user.email === field.email && user.store_id === field.store_id
          );
          const showDeleteButton = !isCurrentUser && (store.users.length > 1 || !isExistingUser);

          return (
            <Row key={field.id}>
              <Cell>
                {isExistingUser && (
                  <span className="flex items-center h-8 text-gray-500">{field.email}</span>
                )}
                {!isExistingUser && (
                  <Field
                    label=""
                    name={`users.${index}.email`}
                    errorMessage={errors.users?.[index]?.email?.message}
                  >
                    <input
                      {...register(`users.${index}.email`)}
                      className={
                        errors.users?.[index]?.email?.message ? inputInvalidClass : inputClass
                      }
                      placeholder="jane@example.com"
                      type="email"
                    />
                  </Field>
                )}
              </Cell>
              <Cell>
                {isExistingUser && (
                  <span className="flex items-center h-8 text-gray-500">
                    {UserRoleLabel[field.relation] ?? field.relation}
                  </span>
                )}
                {!isExistingUser && (
                  <Field
                    label=""
                    name={`users.${index}.relation`}
                    errorMessage={errors.users?.[index]?.relation?.message}
                  >
                    <select
                      {...register(`users.${index}.relation`)}
                      className={
                        errors.users?.[index]?.relation?.message ? inputInvalidClass : inputClass
                      }
                    >
                      {Object.values(UserRole)
                        .filter((role) => role !== UserRole.Demo)
                        .map((role) => (
                          <option key={role} value={role}>
                            {UserRoleLabel[role]}
                          </option>
                        ))}
                    </select>
                  </Field>
                )}
              </Cell>
              <Cell>
                {showDeleteButton && (
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md text-icon hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-600"
                    onClick={() => remove(index)}
                    title="Remove user"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </Cell>
            </Row>
          );
        })}
      </Table>
      <SmallSecondaryButton
        type="button"
        onClick={() => append({ store_id: store.id, email: '', relation: UserRole.Employee })}
        disabled={fields.length >= 10}
      >
        Add User {fields.length >= 10 && '(Maximum 10 users)'}
      </SmallSecondaryButton>
    </div>
  );
}
